"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  CviWorking,
  LynnTable,
  lynnText,
  ratingLabel,
} from "@/components/tools/research-design/cvi-parts";
import { DataInput } from "@/components/tools/research-design/data-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  analyzeContentValidity,
  type CviItem,
  readRatings,
} from "@/lib/tool-kit/research-design/cvi";
import {
  DASH,
  decimals,
  type NumberFormatter,
  percentText,
} from "@/lib/tool-kit/research-design/format";
import {
  isNumericCell,
  labelsFor,
  parseTable,
  toCsv,
  transposeTable,
} from "@/lib/tool-kit/research-design/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const CONTENT_VALIDITY_INDEX_CALCULATOR_STRINGS = {
  dataLabel: "Relevance ratings from 1 to 4",
  dataHint:
    "Paste from a spreadsheet or type ratings separated by commas or spaces: 1 = not relevant, 2 = somewhat relevant, 3 = quite relevant, 4 = highly relevant. Blank cells are skipped.",
  dataPlaceholder: "Expert\tItem 1\tItem 2\tItem 3\nE1\t4\t3\t2\nE2\t3\t4\t2",
  loadExample: "Load example",
  clear: "Clear",
  exampleData:
    "Expert\tItem 1\tItem 2\tItem 3\tItem 4\tItem 5\tItem 6\tItem 7\tItem 8\nE1\t4\t3\t4\t2\t4\t3\t4\t2\nE2\t4\t4\t3\t3\t4\t4\t4\t1\nE3\t3\t4\t4\t3\t4\t3\t2\t3\nE4\t4\t4\t4\t2\t3\t4\t4\t2\nE5\t4\t3\t4\t4\t4\t4\t3\t3\nE6\t3\t4\t3\t3\t4\t2\t4\t4",
  firstRowHeader: "First row holds names",
  firstColumnIds: "First column holds names",
  showParsed: "Show the table as read",
  previewNote: "Showing the first {shown} of {total} rows.",
  idHeader: "Name",
  orientationLegend: "Each row of the table is",
  rowsAreExperts: "One expert",
  rowsAreItems: "One item",
  readAs: "Read as {experts} rating {items}.",
  expertCount: "one: {count} expert|other: {count} experts",
  itemCount: "one: {count} item|other: {count} items",
  expertFallback: "Expert {n}",
  itemFallback: "Item {n}",
  results: "Results",
  enterPrompt:
    "Paste the panel's ratings, or load the example, to see every item's I-CVI and the scale-level CVIs.",
  missingNote:
    "one: {count} blank rating was skipped; that item's I-CVI uses the experts who rated it.|other: {count} blank ratings were skipped; each item's I-CVI uses the experts who rated it.",
  invalidNote:
    "one: {count} cell is not a whole-number rating from 1 to 4, so it was skipped.|other: {count} cells are not whole-number ratings from 1 to 4, so they were skipped.",
  sCviAve: "S-CVI/Ave",
  sCviUa: "S-CVI/UA",
  experts: "Experts",
  items: "Items",
  scaleNote:
    "S-CVI/Ave is the mean of the item I-CVIs — Polit and Beck (2006) suggest 0.90 or higher. S-CVI/UA is the share of items every expert rated 3 or 4; it is stricter and falls as the panel grows, and 0.80 is the usual target.",
  colItem: "Item",
  colExperts: "Experts",
  colAgree: "Rated 3 or 4",
  colIcvi: "I-CVI",
  colPc: "Pc",
  colKappa: "k*",
  colRating: "k* evaluation",
  colLynn: "Lynn (1986)",
  ratingExcellent: "Excellent",
  ratingGood: "Good",
  ratingFair: "Fair",
  ratingPoor: "Poor",
  lynnMet: "Meets ({agree} of {experts})",
  lynnMissed: "Below ({agree} of {experts} needed)",
  lynnTooFew: "Needs 3+ experts",
  kappaNote:
    "k* adjusts each I-CVI for chance agreement (Polit, Beck & Owen, 2007): excellent above 0.74, good 0.60–0.74, fair 0.40–0.59, poor below 0.40.",
  workingFor: "Show the working for",
  workingIcvi: "I-CVI = A ÷ N = {agree} ÷ {experts} = {icvi}",
  workingPc:
    "Pc = N! ÷ (A! × (N − A)!) × 0.5ᴺ = {experts}! ÷ ({agree}! × {rest}!) × 0.5{power} = {ways} × {half} = {pc}",
  workingKappa:
    "k* = (I-CVI − Pc) ÷ (1 − Pc) = ({icvi} − {pc}) ÷ (1 − {pc}) = {kappa}",
  workingRating: "k* evaluation: {rating}.",
  workingLynnMet:
    "Lynn's (1986) minimum for {experts} experts is {agree} rating the item 3 or 4. This item has {have}, so it meets the criterion.",
  workingLynnMissed:
    "Lynn's (1986) minimum for {experts} experts is {agree} rating the item 3 or 4. This item has {have}, so it falls short.",
  workingNoLynn: "Lynn's criterion needs at least three experts.",
  lynnHeading: "Lynn's minimum by panel size",
  colPanel: "Experts on the panel",
  colMustAgree: "Must rate 3 or 4",
  colMinimum: "Minimum I-CVI",
  yourPanel: "{experts} (your panel)",
  lynnLarger:
    "Lynn's table stops at ten experts. For a larger panel the tool applies the 0.78 floor Polit and Beck (2006) drew from it — {agree} of your {experts}.",
  expertHeading: "Each expert's share of items rated 3 or 4",
  colExpert: "Expert",
  colShare: "Rated 3 or 4",
  expertNote:
    "With no blank ratings, the mean of these shares equals S-CVI/Ave. An expert far below the others may have misread the task.",
  downloadCsv: "Download item results (CSV)",
  csvFile: "content-validity-index.csv",
} as const;

export type CviStrings = typeof CONTENT_VALIDITY_INDEX_CALCULATOR_STRINGS;
type Orientation = "experts" | "items";

function itemCells(
  name: string,
  item: CviItem | null,
  strings: CviStrings,
  fmt: NumberFormatter
): string[] {
  if (!item) {
    return [name, "0", DASH, DASH, DASH, DASH, DASH, DASH];
  }
  return [
    name,
    fmt(item.experts),
    fmt(item.agree),
    decimals(fmt, item.icvi),
    decimals(fmt, item.pc, 3),
    decimals(fmt, item.kappa),
    ratingLabel(item.rating, strings),
    lynnText(item, strings),
  ];
}

export default function ContentValidityIndexCalculatorWidget() {
  const strings = useToolUi(CONTENT_VALIDITY_INDEX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [header, setHeader] = useState<boolean | null>(null);
  const [ids, setIds] = useState<boolean | null>(null);
  const [orientation, setOrientation] = useState<Orientation>("experts");

  const pasted = parseTable(raw, isNumericCell, {
    header: header ?? undefined,
    ids: ids ?? undefined,
  });
  const table = orientation === "experts" ? pasted : transposeTable(pasted);
  const pastedWidth = Math.max(0, ...pasted.body.map((row) => row.length));
  const itemTotal = Math.max(0, ...table.body.map((row) => row.length));
  const ratings = readRatings(table.body);
  const analysis = analyzeContentValidity(ratings.values, itemTotal);
  const itemNames = labelsFor(table.columns, itemTotal, (n) =>
    t(strings.itemFallback, { n })
  );
  const expertNames = labelsFor(table.ids, table.body.length, (n) =>
    t(strings.expertFallback, { n })
  );
  const pastedNames =
    orientation === "experts"
      ? itemNames
      : labelsFor(pasted.columns, pastedWidth, (n) =>
          t(strings.expertFallback, { n })
        );
  const head = [
    strings.colItem,
    strings.colExperts,
    strings.colAgree,
    strings.colIcvi,
    strings.colPc,
    strings.colKappa,
    strings.colRating,
    strings.colLynn,
  ];

  function reset(text: string) {
    setRaw(text);
    setHeader(null);
    setIds(null);
  }

  function download() {
    const rows = itemNames.map((name, i) =>
      itemCsvRow(name, analysis?.items[i] ?? null, strings)
    );
    downloadText(
      strings.csvFile,
      toCsv([head, ...rows]),
      "text/csv;charset=utf-8"
    );
  }

  return (
    <>
      <DataInput
        columnNames={pastedNames}
        example={strings.exampleData}
        fmt={fmt}
        id="cvi-data"
        onChange={setRaw}
        onHeader={setHeader}
        onIds={setIds}
        onReset={reset}
        strings={strings}
        summary={t(strings.readAs, {
          experts: tPlural(strings.expertCount, locale, table.body.length, {
            count: fmt(table.body.length),
          }),
          items: tPlural(strings.itemCount, locale, itemTotal, {
            count: fmt(itemTotal),
          }),
        })}
        table={pasted}
        value={raw}
      >
        <PillGroup
          label={strings.orientationLegend}
          name="cvi-orientation"
          onChange={setOrientation}
          options={[
            { value: "experts", label: strings.rowsAreExperts },
            { value: "items", label: strings.rowsAreItems },
          ]}
          value={orientation}
        />
      </DataInput>

      <section aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {analysis ? (
          <>
            {ratings.missing > 0 ? (
              <Notice>
                {tPlural(strings.missingNote, locale, ratings.missing, {
                  count: fmt(ratings.missing),
                })}
              </Notice>
            ) : null}
            {ratings.invalid > 0 ? (
              <Notice>
                {tPlural(strings.invalidNote, locale, ratings.invalid, {
                  count: fmt(ratings.invalid),
                })}
              </Notice>
            ) : null}
            <StatGrid>
              <StatCard
                emphasis
                label={strings.sCviAve}
                value={decimals(fmt, analysis.sCviAve)}
              />
              <StatCard
                label={strings.sCviUa}
                value={decimals(fmt, analysis.sCviUa)}
              />
              <StatCard label={strings.experts} value={fmt(analysis.experts)} />
              <StatCard label={strings.items} value={fmt(itemTotal)} />
            </StatGrid>
            <p className={HINT}>{strings.scaleNote}</p>
            <DataTable
              head={head}
              rows={itemNames.map((name, i) =>
                itemCells(name, analysis.items[i] ?? null, strings, fmt)
              )}
            />
            <p className={HINT}>{strings.kappaNote}</p>
            <CviWorking
              fmt={fmt}
              itemNames={itemNames}
              items={analysis.items}
              strings={strings}
            />
            <LynnTable experts={analysis.experts} fmt={fmt} strings={strings} />
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.expertHeading}</h3>
              <DataTable
                head={[strings.colExpert, strings.colShare]}
                rows={expertNames.map((name, i) => [
                  name,
                  percentText(fmt, analysis.expertShares[i] ?? null),
                ])}
              />
              <p className={HINT}>{strings.expertNote}</p>
            </div>
            <button
              className={BUTTON_SECONDARY}
              onClick={download}
              type="button"
            >
              {strings.downloadCsv}
            </button>
          </>
        ) : (
          <Notice>{strings.enterPrompt}</Notice>
        )}
      </section>
    </>
  );
}

function itemCsvRow(
  name: string,
  item: CviItem | null,
  strings: CviStrings
): (string | number | null)[] {
  if (!item) {
    return [name, 0, null, null, null, null, null, null];
  }
  return [
    name,
    item.experts,
    item.agree,
    item.icvi,
    item.pc,
    item.kappa,
    ratingLabel(item.rating, strings),
    lynnText(item, strings),
  ];
}
